import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DragIndicatorOutlined, InfoOutlined } from '@mui/icons-material';
import { Box, IconButton, SxProps, Theme, Tooltip, Typography } from '@mui/material';

import { useLanguage } from '../../hooks';
import { SignatureOrderType, SignatureOrderUser, SignerData } from '../../types/taskForm';
import { paths } from '../../utils/navigation';
import { LinkBreakIcon, TrashIcon } from '../icons';
import { getDraggableId } from './utils';

const customStyles: Record<string, SxProps<Theme>> = {
  dragIndicator: {
    fontSize: '24px',
    fill: (theme) => theme.palette.grey[600],
  },
  orderIndex: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    minWidth: '24px',
    borderRadius: '50%',
    border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
    fontSize: 'inherit',
    fontWeight: '500',
  },
  groupIndex: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    minWidth: '24px',
    borderRadius: '50%',
    border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
    fontSize: 'inherit',
    fontWeight: '500',
    color: (theme) => theme.palette.text.disabled,
    backgroundColor: (theme) => theme.palette.action.hover,
  },
  deleteButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginLeft: 'auto',
  },
  deleteButton: {
    marginLeft: 'auto',
  },
  userTooltipIcon: {
    color: (theme) => theme.palette.common.black,
    fontSize: '16px',
  },
  userContent: {
    minWidth: '0px',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
  },
  tooltipText: {
    fontSize: '12px',
  },
  links: {
    cursor: 'pointer',
    textDecoration: 'underline',
    fontSize: '12px',
  },
  unGroupIcon: {
    height: '14px',
    width: '14px',
    fill: (theme) => theme.palette.primary.main,
  },
};
export interface DragAndDropSignerBoxContentProps {
  orderType: SignatureOrderType;
  isDragDisabled: boolean;
  index: number;
  signers: SignatureOrderUser[];
  signer: SignatureOrderUser[];
  usersData: Record<string, SignerData>;
  onSignerDelete: (signerId: string | number) => void;
  handleUnGroup?: (signerId: string, groupIndex: number) => void;
  onOverflowChange?: (isOverflowed: boolean) => void;
}

export function DragAndDropSignerBoxContentAndActions({
  orderType,
  isDragDisabled,
  index,
  signers,
  signer,
  usersData,
  onSignerDelete,
  handleUnGroup,
  onOverflowChange,
}: DragAndDropSignerBoxContentProps) {
  const lang = useLanguage();
  const navigate = useNavigate();

  if (signer.length === 0) {
    return null;
  }

  const isSimultaneous = orderType === SignatureOrderType.SIMULTANEOUS;
  const actualSigner = signer[0];
  const signerId: string = getDraggableId(actualSigner);

  const showLocationTooltip = Boolean(usersData[signerId]?.is_location);
  const showAuthorTooltip = usersData[signerId]?.id === 'author';
  const isDynamicSigner = usersData[signerId]?.question_id != null;
  const userName = usersData[signerId]?.name || '';

  const textRef = useRef<HTMLDivElement>(null);
  const [textHasMoreThanOneLine, setTextHasMoreThanOneLine] = useState(false);
  useEffect(() => {
    if (isDynamicSigner && textRef.current) {
      const computedStyle = window.getComputedStyle(textRef.current);
      const lineHeight = parseFloat(computedStyle.lineHeight);
      const elementHeight = textRef.current.clientHeight;
      const numberOfLines = Math.round(elementHeight / lineHeight);
      const hasOverflow = numberOfLines >= 2;
      setTextHasMoreThanOneLine(hasOverflow);
      if (onOverflowChange) onOverflowChange(isDynamicSigner && hasOverflow);
    }
  }, [isDynamicSigner, userName, onOverflowChange]);
  const locationUserTooltip = (
    <Tooltip
      title={
        <Typography sx={customStyles.tooltipText}>
          {lang.formSignatureBuilder.locationUserTooltip}{' '}
          <Typography
            onClick={() => navigate(paths.lists.index)}
            sx={customStyles.links}
            component='span'
          >
            {lang.formSignatureBuilder.locationUserViewEmails}
          </Typography>
        </Typography>
      }
    >
      <InfoOutlined sx={customStyles.userTooltipIcon} />
    </Tooltip>
  );

  const authorUserTooltip = (
    <Tooltip title={lang.formSignatureBuilder.authorUserTooltip}>
      <InfoOutlined sx={customStyles.userTooltipIcon} />
    </Tooltip>
  );

  return (
    <>
      {!isDragDisabled && <DragIndicatorOutlined sx={customStyles.dragIndicator} />}

      {!isSimultaneous && (
        <Box
          component='span'
          sx={signers.length > 1 ? customStyles.groupIndex : customStyles.orderIndex}
        >
          {index + 1}
        </Box>
      )}

      {isDynamicSigner && textHasMoreThanOneLine ? (
        <Tooltip
          title={userName}
          placement='bottom'
        >
          <Box sx={customStyles.userContent}>{userName}</Box>
        </Tooltip>
      ) : (
        <Box
          ref={textRef}
          sx={customStyles.userContent}
        >
          {userName}
        </Box>
      )}

      {showLocationTooltip && locationUserTooltip}
      {showAuthorTooltip && authorUserTooltip}

      {isSimultaneous ? (
        <IconButton
          onClick={() => onSignerDelete(signerId)}
          size='small'
          sx={customStyles.deleteButton}
        >
          <TrashIcon
            color='primary'
            fontSize='inherit'
          />
        </IconButton>
      ) : (
        <Box sx={customStyles.deleteButtonContainer}>
          {signers.length > 1 && handleUnGroup && (
            <Tooltip title={lang.formSignatureBuilder.unGroupSigner}>
              <IconButton
                size='small'
                onClick={() => handleUnGroup(signerId, index)}
              >
                <LinkBreakIcon sx={customStyles.unGroupIcon} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={lang.formSignatureBuilder.deleteSigner}>
            <IconButton
              size='small'
              onClick={() => onSignerDelete(signerId)}
            >
              <TrashIcon
                color='primary'
                fontSize='inherit'
              />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </>
  );
}
